import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { CustomValidators } from '@core-lib/classes';
import { AppConstants } from '@core-lib/config';
import { formatMontant } from '@core-lib/functions';

export type InputRestrictionType =
	| 'numeric'
	| 'alpha'
	| 'alphanumeric'
	| 'decimal'
	| 'numericChars'
	| 'numericwithoutformat'
	| 'positiveOrNegativeInt'
	| 'telephoneformat'
	| 'nomEmetteur'
	| 'configuredDecimal'
	| 'configuredDecimalSigned'
	| 'numericWithwhiteSpace'
	| 'alphanumericiban'
	| 'datePicker';

@Directive({
    selector: '[inputRestriction]',
    standalone: false
})
export class InputRestrictionDirective {
	@Input('inputRestriction') inputRestriction: InputRestrictionType;
	@Input('inputRestrictionAllowNegative') allowNegative: boolean = false;
	@Input('formControl') formControl: AbstractControl;
	@Input('controlLengthNeuf') controlLengthNeuf: boolean = false;
	@Input('controlLengthSiret') controlLengthSiret: boolean = false;
	@Input('controlFormatTVA') controlFormatTVA: boolean = false;
	@Input('controlTelephoneLength') controlTelephoneLength: boolean = false;
	@Input('controlFormatRcs') controlFormatRcs: boolean = false;
	@Input('controlLengthRegistreEquivalent') controlLengthRegistreEquivalent: boolean = false;
	@Input('fieldFormControl') fieldFormControl: AbstractControl;
	@Input('withAmountSeparation') withAmountSeparation: boolean = false;

	@Input()
	set configurationNumber(value: number) {
		this._configurationNumber = value;
		const currentValue = this.inputElement.value;
		if (currentValue != '') {
			this.cleanDecimal(currentValue, value);
		}
	}
	private _configurationNumber: number;
	get configurationNumber(): number {
		return this._configurationNumber;
	}

	inputElement: HTMLInputElement;

	constructor(private el: ElementRef) {
		this.inputElement = el.nativeElement;
	}

	@HostListener('keyup', ['$event']) onKeyUp(e: KeyboardEvent): void {
		if (
			(this.controlLengthNeuf || this.controlLengthSiret || this.controlFormatRcs || this.controlLengthRegistreEquivalent) &&
			this.fieldFormControl
		) {
			this.fieldFormControl?.setValidators(Validators.required);
		}

		if (this.inputRestriction == 'nomEmetteur' && !this.isNomEmetteurValid(this.inputElement.value)) {
			this.formControl?.patchValue(`${this.inputElement.value}`.replace(AppConstants.REGEX.filterNomEmetteur, ''));
		}
	}

	@HostListener('paste', ['$event']) onPaste(e: ClipboardEvent): void {
		setTimeout(() => {
			if (this.inputRestriction == 'nomEmetteur' && !this.isNomEmetteurValid(this.inputElement.value)) {
				this.formControl?.patchValue(`${this.inputElement.value}`.replace(AppConstants.REGEX.filterNomEmetteur, ''));
			}
		}, 0); // setTimeout pour attendre que la valeur collée soit présente
	}

	@HostListener('keypress', ['$event']) onKeyPress(e: KeyboardEvent): void {
		const key = e.key;
		const currentValue: string = this.inputElement.value ?? this.inputElement.textContent;

		const start = this.inputElement.selectionStart;
		const end = this.inputElement.selectionEnd;
		const targetValue: string = [currentValue.slice(0, start), key, currentValue.slice(end)].join('');

		if (!targetValue) {
			e.preventDefault();
		}

		if (this.controlLengthNeuf && this.fieldFormControl) {
			this.fieldFormControl?.clearValidators();
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		}

		if (this.controlLengthSiret && this.fieldFormControl) {
			this.fieldFormControl?.clearValidators();
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		}

		if ((this.controlFormatRcs || this.controlLengthRegistreEquivalent) && this.fieldFormControl) {
			this.fieldFormControl?.clearValidators();
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		}

		if (this.controlFormatTVA && this.fieldFormControl) {
			this.fieldFormControl?.clearValidators();
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		}

		if (this.controlTelephoneLength && this.fieldFormControl) {
			this.fieldFormControl?.clearValidators();
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		}

		switch (this.inputRestriction) {
			case 'numeric':
				if (!this.isNumber(targetValue)) {
					e.preventDefault();
				}
				break;

			case 'numericChars':
				if (!this.isNumericChar(targetValue)) {
					e.preventDefault();
				}
				break;

			case 'alpha':
				if (!this.isAlpha(targetValue)) {
					e.preventDefault();
				}
				break;

			case 'alphanumeric':
				if (!this.isAlphanumeric(targetValue)) {
					e.preventDefault();
				}
				break;

			case 'alphanumericiban':
				if (!this.isAlphanumericIban(targetValue)) {
					e.preventDefault();
				}
				break;

			case 'decimal':
				if (!this.isDecimal(targetValue)) {
					e.preventDefault();
				}
				break;
			case 'configuredDecimal':
				if (!this.isConfiguredDecimal(targetValue, this.configurationNumber)) {
					e.preventDefault();
				}
				break;
			case 'configuredDecimalSigned':
				if (!this.isConfiguredDecimalSigned(targetValue, this.configurationNumber)) {
					e.preventDefault();
				}
				break;
			case 'numericwithoutformat':
				if (!this.isNumericwithoutformat(targetValue)) {
					e.preventDefault();
				}
				break;
			case 'positiveOrNegativeInt':
				if (!this.isPositiveOrNegativeInt(targetValue)) {
					e.preventDefault();
				}
				break;
			case 'telephoneformat':
				if (!this.isTelephone(targetValue)) {
					e.preventDefault();
				}
				break;
			case 'nomEmetteur':
				if (!this.isNomEmetteurValid(targetValue)) {
					e.preventDefault();
				}
				break;
			case 'numericWithwhiteSpace':
				if (!this.isPositiveOrNegativeInt(targetValue)) {
					e.preventDefault();
				}
				break;
			case 'datePicker':
				if (!this.isDateInputValid(targetValue)) {
					e.preventDefault();
				}
				break;
		}
	}

	@HostListener('blur', ['$event']) onBlur(e): void {
		const value: string = this.inputElement.value;

		if (!value) {
			return;
		} else if (this.controlLengthNeuf) {
			if (value.length != 9) {
				this.fieldFormControl?.setValidators(CustomValidators.wrongSirenLength());
			} else {
				this.fieldFormControl?.clearValidators();
			}
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		} else if (this.controlLengthSiret) {
			if (value.length != 14) {
				this.fieldFormControl?.setValidators(CustomValidators.wrongSiretLength());
			} else {
				this.fieldFormControl?.clearValidators();
			}
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		} else if (this.controlLengthRegistreEquivalent) {
			if (value.length > 50) {
				this.fieldFormControl?.setValidators(CustomValidators.wrongRegistreEquivalentLength());
			} else {
				this.fieldFormControl?.clearValidators();
			}
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		} else if (this.controlFormatRcs) {
			// Define a regular expression to match the pattern
			const pattern = /^(\d{3}\s?\d{3}\s?\d{3}|^\d{9})\s?RCS\s\w+/;

			// Use test() method to check if the input matches the pattern
			if (!pattern.test(value)) {
				this.fieldFormControl?.setValidators(CustomValidators.wrongRcsFormat());
			} else {
				this.fieldFormControl?.clearValidators();
			}
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		} else if (this.controlFormatTVA) {
			// Define a regular expression to match the pattern
			const pattern = /^(\d{3}\s?\d{3}\s?\d{3}|^\d{9})\s?RCS\s\w+/;

			// Use test() method to check if the input matches the pattern
			if (!pattern.test(value)) {
				this.fieldFormControl?.setValidators(CustomValidators.wrongTVAFormat());
			} else {
				this.fieldFormControl?.clearValidators();
			}
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		} else if (this.controlTelephoneLength) {
			if (value.length != 10) {
				this.fieldFormControl?.setValidators(CustomValidators.wrongTelephoneLength());
			} else {
				this.fieldFormControl?.clearValidators();
			}
			this.fieldFormControl.updateValueAndValidity({ emitEvent: false });
		}

		switch (this.inputRestriction) {
			case 'configuredDecimal':
			case 'configuredDecimalSigned':
			case 'decimal':
				this.cleanDecimal(value, this.configurationNumber);
				break;
		}
	}

	@HostListener('focus', ['$event']) onFocus($event) {
		if (!!this.withAmountSeparation) {
		$event.target.value = this.inputElement.value?.replace(/\s/g, '');
		}
	}

	isNumber(value: string): boolean {
		return this.allowNegative ? AppConstants.REGEX.numericNegative.test(value) : AppConstants.REGEX.numeric.test(value);
	}

	isNomEmetteurValid(value: string): boolean {
		return AppConstants.REGEX.nomEmetteur.test(value);
	}

	isNumericChar(value: string): boolean {
		return AppConstants.REGEX.numericChars.test(value);
	}

	isAlpha(value: string): boolean {
		return AppConstants.REGEX.alpha.test(value);
	}

	isAlphanumeric(value: string): boolean {
		return AppConstants.REGEX.alphanumeric.test(value);
	}

	isAlphanumericIban(value: string): boolean {
		return AppConstants.REGEX.alphanumericiban.test(value);
	}

	isDecimal(value: string): boolean {
		return this.allowNegative ? AppConstants.REGEX.decimalNegative.test(value) : AppConstants.REGEX.decimal.test(value);
	}

	isConfiguredDecimal(value: string, configurationNumber: number): boolean {
		let decimalRegex =
			configurationNumber != null && configurationNumber != undefined
				? configurationNumber == 0
					? AppConstants.REGEX.numericWithoutDot
					: new RegExp(`^[0-9]{1,}([\\.,][0-9]{0,${configurationNumber}})?$`)
				: AppConstants.REGEX.decimal;
		return decimalRegex.test(value);
	}

	isConfiguredDecimalSigned(value: string, configurationNumber: number): boolean {
		let decimalRegex =
			configurationNumber != null && configurationNumber != undefined
				? configurationNumber == 0
					? new RegExp(`^(-?)[0-9]*$`)
					: new RegExp(`^(-?)[0-9]*([\\.,][0-9]{0,${configurationNumber}})?$`)
				: new RegExp(`^(-?)[0-9]*([\\.,][0-9]{0,2})?$`);
		return decimalRegex.test(value);
	}

	isNumericwithoutformat(value: string): boolean {
		return this.allowNegative
			? AppConstants.REGEX.numericWithoutDotNegative.test(value)
			: AppConstants.REGEX.numericWithoutDot.test(value);
	}

	isPositiveOrNegativeInt(value: string): boolean {
		return AppConstants.REGEX.intPositiveOrNegative.test(value);
	}

	cleanDecimal(value: string, configurationNumber?: number): void {
		const targetValue = formatMontant(value, configurationNumber, this.withAmountSeparation);
		this.inputElement.value = targetValue;
	}

	isTelephone(value: string): boolean {
		return AppConstants.REGEX.telephone.test(value);
	}

	isNumericWithwhiteSpace(value: string): boolean {
		return AppConstants.REGEX.numericWithwhiteSpace.test(value);
	}

	isDateInputValid(value: string): boolean {
		return AppConstants.REGEX.datePicker.test(value);
	}
}
